import { BreakPointFunction, MakeLogicType, connect, kea, listeners, path } from 'kea'
import { loaders } from 'kea-loaders'

import { FEATURE_FLAGS } from 'lib/constants'
import { FeatureFlagsSet, featureFlagLogic } from 'lib/logic/featureFlagLogic'
import posthog from 'lib/posthog-typed'
import { userLogic } from 'scenes/userLogic'

import { dashboardsModel } from '~/models/dashboardsModel'
import { AvailableFeature } from '~/types'

import { ExportNudgeSubject } from 'products/subscriptions/frontend/components/Subscriptions/exportNudge/exportNudgeSubject'
import {
    fetchHasSubscriptionForDashboard,
    fetchHasSubscriptionForInsight,
    fetchTeamSubscriptionCount,
} from 'products/subscriptions/frontend/components/Subscriptions/subscriptionQueries'
import { subscriptionsLogic } from 'products/subscriptions/frontend/components/Subscriptions/subscriptionsLogic'
import {
    SubscriptionBaseProps,
    canNudgeToSubscribe,
} from 'products/subscriptions/frontend/components/Subscriptions/utils'

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface exportNudgeLogicValues {
    featureFlags: FeatureFlagsSet // featureFlagLogic
    hasAvailableFeature: (feature: AvailableFeature, currentUsage?: number | undefined) => boolean // userLogic
    freeTierSubscriptionCount: number | null
    freeTierSubscriptionCountLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface exportNudgeLogicActions {
    loadFreeTierSubscriptionCount: (_?: unknown) => unknown
    loadFreeTierSubscriptionCountFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadFreeTierSubscriptionCountSuccess: (
        freeTierSubscriptionCount: number,
        payload?: unknown
    ) => {
        freeTierSubscriptionCount: number
        payload?: unknown
    }
}

export type exportNudgeLogicType = MakeLogicType<exportNudgeLogicValues, exportNudgeLogicActions>

export const exportNudgeLogic = kea<exportNudgeLogicType>([
    path(['products', 'subscriptions', 'exportNudgeLogic']),
    connect(() => ({
        values: [featureFlagLogic, ['featureFlags'], userLogic, ['hasAvailableFeature']],
    })),
    loaders(() => ({
        // The `load` prefix puts this on initKea's ERROR_FILTER_ALLOW_LIST, so a failed background
        // check doesn't toast at a user who never asked for anything.
        freeTierSubscriptionCount: [
            null as number | null,
            {
                loadFreeTierSubscriptionCount: async (_?: unknown, breakpoint?: BreakPointFunction) =>
                    await fetchTeamSubscriptionCount(breakpoint),
            },
        ],
    })),
    listeners(() => ({
        loadFreeTierSubscriptionCountFailure: ({ error, errorObject }) => {
            // A failed count excludes a free-tier user, and the readout cannot tell that apart from
            // being at the limit unless it is recorded.
            captureExportNudgeCheckFailed('limit', {
                error_name: errorObject?.name,
                error_status: errorObject?.status,
                error_message: error,
            })
        },
    })),
])

export interface ExportNudgeCandidate {
    subject: ExportNudgeSubject
    name: string | null
}

export type ExportNudgeLookup =
    | { status: 'eligible'; candidate: ExportNudgeCandidate }
    | { status: 'ineligible' }
    | { status: 'unknown' }

type SubjectOfKind<K extends ExportNudgeSubject['kind']> = Extract<ExportNudgeSubject, { kind: K }>

interface SubjectKindSupport<K extends ExportNudgeSubject['kind']> {
    /** Event names are spelled out so the string a metric is configured against can be grepped. */
    flag: keyof FeatureFlagsSet
    shownEvent: string
    /** How the subscriptions code addresses this subject: the mounted logic's key, and the CTA url. */
    target: (subject: SubjectOfKind<K>) => SubscriptionBaseProps
    hasSubscription: (subject: SubjectOfKind<K>) => Promise<boolean>
    eventProperties: (subject: SubjectOfKind<K>) => Record<string, string | number>
    /** Null where the app holds no name for it, and the offer's copy falls back on `noun`. */
    name: (subject: SubjectOfKind<K>) => string | null
    noun: string
}

// One place to add a subject kind. Everything that differs per kind lives here rather than in a
// ternary at each call site.
const SUBJECT_SUPPORT: { [K in ExportNudgeSubject['kind']]: SubjectKindSupport<K> } = {
    dashboard: {
        flag: FEATURE_FLAGS.DASHBOARD_EXPORT_NUDGE,
        shownEvent: 'dashboard export nudge shown',
        target: (subject) => ({ dashboardId: subject.dashboardId }),
        hasSubscription: (subject) => fetchHasSubscriptionForDashboard(subject.dashboardId),
        eventProperties: (subject) => ({ kind: subject.kind, dashboard_id: subject.dashboardId }),
        name: (subject) => dashboardsModel.findMounted()?.values.rawDashboards[subject.dashboardId]?.name ?? null,
        noun: 'this dashboard',
    },
    insight: {
        flag: FEATURE_FLAGS.INSIGHT_EXPORT_NUDGE,
        shownEvent: 'insight export nudge shown',
        target: (subject) => ({ insightShortId: subject.insightShortId }),
        hasSubscription: (subject) => fetchHasSubscriptionForInsight(subject.insightShortId),
        eventProperties: (subject) => ({ kind: subject.kind, insight_short_id: subject.insightShortId }),
        // There is no light-weight model of insights to read a name from, and importing the insight
        // scene here would put it in every export surface's import graph.
        name: () => null,
        noun: 'this insight',
    },
}

// Each entry is written against its own variant, so it needs no casts. This lookup is the one place
// that widens back to the union, which is sound because the record is keyed by the same `kind`.
const supportFor = (subject: ExportNudgeSubject): SubjectKindSupport<ExportNudgeSubject['kind']> =>
    SUBJECT_SUPPORT[subject.kind] as unknown as SubjectKindSupport<ExportNudgeSubject['kind']>

/** What the offer calls a subject with no name of its own. */
export function subjectNoun(subject: ExportNudgeSubject): string {
    return supportFor(subject).noun
}

export function captureExportNudgeCheckFailed(
    step: 'limit' | 'check' | 'timeout' | 'toast-gone',
    properties: Record<string, unknown> = {}
): void {
    posthog.capture('export nudge check failed', { step, ...properties })
}

// A healthy check that lands on "not eligible" is not a failure, so it gets its own event: without
// this, the only visible ineligible exporters were the ones whose check broke.
function captureExportNudgeIneligible(
    reason: 'already_subscribed' | 'over_limit',
    properties: Record<string, unknown> = {}
): void {
    posthog.capture('export nudge not eligible', { reason, ...properties })
}

// Mounted on first use, so exports of anything else do not pay for it, and left mounted so the
// free-tier count survives between exports.
function mountedExportNudgeLogic(): typeof exportNudgeLogic {
    if (!exportNudgeLogic.isMounted()) {
        exportNudgeLogic.mount()
    }
    return exportNudgeLogic
}

export function exportNudgeEventProperties(subject: ExportNudgeSubject): Record<string, string | number> {
    return supportFor(subject).eventProperties(subject)
}

export function subscriptionTargetFor(subject: ExportNudgeSubject): SubscriptionBaseProps {
    return supportFor(subject).target(subject)
}

// The subscribe button on both scenes shows a count, which mounts subscriptionsLogic and loads the
// subject's subscriptions, so most exports need no request of their own. The count only renders for
// teams that have the feature, so free-tier teams fall through to the network check. A failed load
// reads as an empty list here, so this path cannot fail closed.
function loadedSubscriptionCount(subject: ExportNudgeSubject): number | null {
    const mounted = subscriptionsLogic.findMounted(subscriptionTargetFor(subject))
    if (!mounted || mounted.values.subscriptionsLoading) {
        return null
    }
    return mounted.values.subscriptions.length
}

/** `unknown` means the team's count has not loaded, which is not the same as being over the cap. */
function subscriptionLimitStatus(): 'within' | 'exceeded' | 'unknown' {
    const { values } = mountedExportNudgeLogic()
    const hasFeature = values.hasAvailableFeature(AvailableFeature.SUBSCRIPTIONS)
    if (!hasFeature && values.freeTierSubscriptionCount === null) {
        return 'unknown'
    }
    return canNudgeToSubscribe(hasFeature, values.freeTierSubscriptionCount) ? 'within' : 'exceeded'
}

/** null means the check itself failed. */
async function fetchHasExistingSubscription(subject: ExportNudgeSubject): Promise<boolean | null> {
    try {
        return await supportFor(subject).hasSubscription(subject)
    } catch (error: any) {
        captureExportNudgeCheckFailed('check', {
            ...exportNudgeEventProperties(subject),
            error_name: error?.name,
            error_status: error?.status,
            error_message: error?.message ?? String(error),
        })
        return null
    }
}

export function lookUpExportNudge(subject: ExportNudgeSubject): ExportNudgeLookup {
    const limitStatus = subscriptionLimitStatus()
    if (limitStatus !== 'within') {
        if (limitStatus === 'exceeded') {
            captureExportNudgeIneligible('over_limit', exportNudgeEventProperties(subject))
        }
        return { status: limitStatus === 'unknown' ? 'unknown' : 'ineligible' }
    }

    const subscriptionCount = loadedSubscriptionCount(subject)
    if (subscriptionCount === null) {
        return { status: 'unknown' }
    }
    if (subscriptionCount > 0) {
        captureExportNudgeIneligible('already_subscribed', exportNudgeEventProperties(subject))
        return { status: 'ineligible' }
    }
    return { status: 'eligible', candidate: { subject, name: supportFor(subject).name(subject) } }
}

/** Runs alongside the export, for a subject whose scene had not already loaded the answer. */
export async function resolveExportNudgeEligibility(subject: ExportNudgeSubject): Promise<ExportNudgeCandidate | null> {
    const { values, asyncActions } = mountedExportNudgeLogic()
    if (!values.hasAvailableFeature(AvailableFeature.SUBSCRIPTIONS) && values.freeTierSubscriptionCount === null) {
        await asyncActions.loadFreeTierSubscriptionCount()
    }
    const limitStatus = subscriptionLimitStatus()
    if (limitStatus === 'unknown') {
        // A second export can dispatch the same loader and breakpoint this one, so the count can
        // still be unresolved here without any request failing. Reported because every other way
        // the offer disappears reports a step.
        captureExportNudgeCheckFailed('limit', exportNudgeEventProperties(subject))
        return null
    }
    if (limitStatus !== 'within') {
        captureExportNudgeIneligible('over_limit', exportNudgeEventProperties(subject))
        return null
    }

    const hasExistingSubscription = await fetchHasExistingSubscription(subject)
    if (hasExistingSubscription === null) {
        return null // fail closed on a broken check
    }
    if (hasExistingSubscription) {
        captureExportNudgeIneligible('already_subscribed', exportNudgeEventProperties(subject))
        return null
    }

    return { subject, name: supportFor(subject).name(subject) }
}

/**
 * Last gate, run as the nudge renders so an exposure is never reported for a toast nobody saw.
 * Returns whether this exporter is in the treatment.
 */
export function claimExportNudge(subject: ExportNudgeSubject): boolean {
    const { values } = mountedExportNudgeLogic()
    // Reading the flag fires the exposure event, so it goes last, once every other check passed.
    if (values.featureFlags[supportFor(subject).flag] !== 'test') {
        return false
    }

    posthog.captureRaw(supportFor(subject).shownEvent, exportNudgeEventProperties(subject))
    return true
}
